import PptxGenJS from "pptxgenjs"

import { TableExportPayload } from "types/ReportingDashboard/elements.types"

import { CompressionOptions } from "./coordinates"

/**
 * Safely remove control characters that can corrupt PowerPoint files
 */
const sanitizeTextForPowerPoint = (text: string): string => {
  // Remove control characters using a safe approach
  return text
    .replace(/[\0-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "") // eslint-disable-line no-control-regex
    .trim()
}

export const exportTableToPptx = (
  pptSlide: PptxGenJS.Slide,
  elementX: number,
  elementY: number,
  elementWidth: number,
  elementHeight: number,
  tableConfig: TableExportPayload | null | undefined,
  compressionOptions: CompressionOptions = {}
): void => {
  const {
    compressionFactor = 0.9,
    fontSize = 6,
    cellPadding = 2.5,
  } = compressionOptions

  const correctedElementX = Math.max(0, elementX - 0.05)

  try {
    const rawData = tableConfig?.tableData?.content?.data

    if (
      !tableConfig ||
      !tableConfig.tableData ||
      !tableConfig.tableData.content ||
      !Array.isArray(rawData) ||
      rawData.length === 0
    ) {
      console.warn(
        "No valid table data found for export for element ID:",
        tableConfig?.id || "Unknown"
      )
      pptSlide.addText("[Table Data Missing]", {
        x: correctedElementX,
        y: Math.max(0, elementY),
        w: Math.min(elementWidth * compressionFactor, 9),
        h: 0.4,
        fontSize: Math.max(8, fontSize),
        color: "CC0000",
        align: "center",
        valign: "middle",
      } as PptxGenJS.TextPropsOptions)
      return
    }

    // Enhanced data validation and sanitization
    const sanitizedDataForPptx: PptxGenJS.TableRow[] = []
    let maxColumns = 0

    // First pass: determine max columns and validate data
    rawData.forEach((row) => {
      if (Array.isArray(row) && row.length > 0) {
        maxColumns = Math.max(maxColumns, row.length)
      }
    })

    if (maxColumns === 0) {
      console.warn("No valid columns found in table data")
      pptSlide.addText("[No Table Columns]", {
        x: correctedElementX,
        y: Math.max(0, elementY),
        w: Math.min(elementWidth * compressionFactor, 9),
        h: 0.4,
        fontSize: Math.max(8, fontSize),
        color: "CC0000",
        align: "center",
        valign: "middle",
      } as PptxGenJS.TextPropsOptions)
      return
    }

    // Second pass: sanitize and normalize data
    rawData.forEach((row) => {
      if (!Array.isArray(row)) return

      const newPptxRow: PptxGenJS.TableCell[] = []
      const isMainTotalsRow = row[0] === "Totals"
      const rowType =
        row.length > 1 && row[1] != null ? String(row[1]).toLowerCase() : ""

      // Ensure all rows have the same number of columns
      for (let j = 0; j < maxColumns; j++) {
        const cell = j < row.length ? row[j] : null
        let formattedCellText: string = ""

        if (cell == null || cell === undefined) {
          formattedCellText = ""
        } else if (typeof cell === "string") {
          // Remove any problematic characters that might break PowerPoint
          formattedCellText = sanitizeTextForPowerPoint(cell)
        } else if (typeof cell === "number") {
          // Enhanced number formatting
          if (isNaN(cell) || !isFinite(cell)) {
            formattedCellText = ""
          } else if (rowType === "row%" || rowType === "col%") {
            const isLastColumn = j === maxColumns - 1
            if (isLastColumn && (cell === 1 || cell === 100)) {
              formattedCellText = "100.0%"
            } else if (cell >= 0 && cell <= 1 && cell !== 1) {
              formattedCellText = (cell * 100).toFixed(1) + "%"
            } else if (cell > 1 && cell <= 100) {
              formattedCellText = cell.toFixed(1) + "%"
            } else {
              formattedCellText = Number.isInteger(cell)
                ? String(cell)
                : cell.toFixed(1)
            }
          } else if (
            rowType === "index" ||
            rowType === "n" ||
            rowType === "N"
          ) {
            formattedCellText = Math.round(cell).toString()
          } else {
            formattedCellText = Number.isInteger(cell)
              ? String(cell)
              : cell.toFixed(1)
          }

          // Handle totals row special formatting
          if (isMainTotalsRow && j > 1) {
            if (cell === 1 || cell === 100) {
              formattedCellText = "100.0%"
            } else if (cell > 0 && cell < 1) {
              formattedCellText = (cell * 100).toFixed(1) + "%"
            } else if (cell > 1 && cell <= 100) {
              formattedCellText = cell.toFixed(1) + "%"
            }
          }
        } else {
          formattedCellText = sanitizeTextForPowerPoint(String(cell))
        }

        // Ensure text is not too long for PowerPoint
        if (formattedCellText.length > 1000) {
          formattedCellText = formattedCellText.substring(0, 997) + "..."
        }

        newPptxRow.push({ text: formattedCellText })
      }

      if (newPptxRow.length > 0) {
        sanitizedDataForPptx.push(newPptxRow)
      }
    })

    // Validate final table structure
    if (sanitizedDataForPptx.length === 0) {
      console.warn("No valid rows found after sanitization")
      pptSlide.addText("[No Valid Table Data]", {
        x: correctedElementX,
        y: Math.max(0, elementY),
        w: Math.min(elementWidth * compressionFactor, 9),
        h: 0.4,
        fontSize: Math.max(8, fontSize),
        color: "CC0000",
        align: "center",
        valign: "middle",
      } as PptxGenJS.TextPropsOptions)
      return
    }

    // Enhanced column width calculation
    const compressedWidth = Math.min(elementWidth * compressionFactor, 9.5) // Max table width
    const maxContentLength: number[] = Array(maxColumns).fill(0)

    sanitizedDataForPptx.forEach((row) => {
      if (Array.isArray(row)) {
        row.forEach((cellObject, j) => {
          if (
            j < maxColumns &&
            cellObject &&
            typeof cellObject === "object" &&
            "text" in cellObject
          ) {
            const cellText = String(cellObject.text || "")
            maxContentLength[j] = Math.max(maxContentLength[j], cellText.length)
          }
        })
      }
    })

    // Improved column width calculation
    const widthFactors = maxContentLength.map((length, index) => {
      if (index === 0) return Math.max(2.0, Math.min(4.0, length * 0.15)) // First column
      if (index === 1) return Math.max(1.0, Math.min(2.0, length * 0.12)) // Second column
      return Math.max(0.8, Math.min(2.5, 0.8 + length * 0.1)) // Other columns
    })

    const totalFactors = widthFactors.reduce((sum, factor) => sum + factor, 0)
    const colWidths: number[] =
      totalFactors > 0
        ? widthFactors.map((factor) =>
            Math.max(0.5, (factor / totalFactors) * compressedWidth)
          )
        : Array(maxColumns).fill(compressedWidth / maxColumns)

    // Ensure column widths are valid
    const minColWidth = 0.5
    const adjustedColWidths = colWidths.map((width) =>
      Math.max(minColWidth, Math.min(width, 3.0))
    )

    // Calculate safe positioning and dimensions
    const safeX = Math.max(0, Math.min(correctedElementX, 9.5))
    const safeY = Math.max(0, Math.min(elementY, 5.0))
    const safeWidth = Math.min(compressedWidth, 10 - safeX)

    const rowCount = sanitizedDataForPptx.length
    const safeFontSize = Math.max(5, Math.min(fontSize, 12))
    const safeRowHeight =
      Math.max(0.15, Math.min(0.25, 4 / Math.max(1, rowCount))) *
      compressionFactor

    // Create table options with enhanced validation
    const tableOptions: PptxGenJS.TableProps = {
      x: safeX,
      y: safeY,
      w: safeWidth,
      fontSize: safeFontSize,
      border: { pt: 0.3, color: "CCCCCC" },
      colW: adjustedColWidths,
      rowH: safeRowHeight,
      align: "center",
      valign: "middle",
      margin: Math.max(1, Math.min(cellPadding, 5)),
      fill: { color: "F9F9F9" },
    }

    // Add table with enhanced error handling
    try {
      pptSlide.addTable(sanitizedDataForPptx, tableOptions)
    } catch (tableError) {
      console.error("Primary table creation failed:", tableError)

      // Fallback: Try with minimal options
      try {
        const minimalOptions: PptxGenJS.TableProps = {
          x: safeX,
          y: safeY,
          w: safeWidth,
          fontSize: 8,
          border: { pt: 0.5, color: "000000" },
          rowH: 0.2,
          align: "left",
          valign: "top",
        }
        pptSlide.addTable(sanitizedDataForPptx, minimalOptions)
      } catch (fallbackError) {
        console.error("Fallback table creation also failed:", fallbackError)

        // Last resort: Add as text
        pptSlide.addText("[Table Export Error - See Console]", {
          x: safeX,
          y: safeY,
          w: safeWidth,
          h: 0.5,
          fontSize: safeFontSize,
          color: "CC0000",
          align: "center",
          valign: "middle",
        } as PptxGenJS.TextPropsOptions)
      }
    }
  } catch (outerError) {
    console.error("Critical error in exportTableToPptx:", outerError)

    // Emergency fallback
    const emergencyX = Math.max(0, Math.min(elementX, 9))
    const emergencyY = Math.max(0, Math.min(elementY, 5))

    pptSlide.addText("[Table Export Failed]", {
      x: emergencyX,
      y: emergencyY,
      w: Math.min(elementWidth * 0.9, 8),
      h: 0.5,
      fontSize: 10,
      color: "CC0000",
      align: "center",
      valign: "middle",
    } as PptxGenJS.TextPropsOptions)
  }
}
